import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, LinearProgress, Paper, Switch, TablePagination, TextField, Typography } from "@mui/material";
import { ARTICLE_TEMPLATE_COLUMNS } from "./articleImportSchema";
import { createImportLookupIndexes, findImportDuplicates, getImportRowErrors, LOOKUP_FIELDS, normalizeImportName, validateImportRow } from "./articleImportModel";
import "./ArticleImportPreview.scss";
import { CheckCircle2, CircleAlert, ListChecks, Clock3, Info, FileCheck2 } from "lucide-react";
import { ARTICLE_IMPORT_STATUS_FILTERS, getArticleImportStatus } from "./articleImportStatus";

import ArticleImportTreeGrid from "./ArticleImportTreeGrid";
import { getImportServerErrors, validateArticleImportBatches } from "./articleImportValidation";
import { requestArticleImportValidation, requestArticleImportSave } from "./articleImportValidationService";
import { isImportRowLocked, isImportRowSaved, saveArticleImportBatches } from "./articleImportSave";
import { addImportCompanyUnit, assignImportCompanyUnit, getImportCompanyUnitDetail, getImportCompanyUnitErrors,
  isImportRowSelected, removeImportCompanyUnit, toggleImportRowSelection, toggleImportRowsSelection, updateImportCompanyUnitDetail } from "./articleImportCompanyUnits";

export default function ArticleImportPreview({ rows, setRows, lookups, showInPos, onShowInPosChange, onBusyChange, importId, saveEnabled = false }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selection, setSelection] = useState({ all: false, ids: new Set() });
  const [expanded, setExpanded] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [validationProgress, setValidationProgress] = useState(null);
  const [validationReport, setValidationReport] = useState(null);
  const [validationStale, setValidationStale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveReport, setSaveReport] = useState(null);
  const bulkJob = useRef(null);
  useEffect(() => () => { bulkJob.current?.abort(); bulkJob.current = null; }, []);
  const companyUnits = useMemo(() => new Map((lookups.companyUnits || [])
    .filter(unit => Number.isInteger(Number(unit.UnitID)) && Number(unit.UnitID) > 0)
    .map(unit => [Number(unit.UnitID), unit])), [lookups]);
  const companyUnitIds = useMemo(() => new Set(companyUnits.keys()), [companyUnits]);
  const indexes = useMemo(() => createImportLookupIndexes(lookups), [lookups]);
  const options = useMemo(() => Object.fromEntries(Object.entries(LOOKUP_FIELDS).map(([field, spec]) => {
    const list = lookups[spec.list] || [];
    const counts = new Map();
    list.forEach(item => {
      const name = normalizeImportName(item[spec.name]);
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    const unique = new Map();
    list.forEach(item => unique.set(Number(item[spec.id]), {
      id: Number(item[spec.id]), name: String(item[spec.name]).trim(),
      label: counts.get(normalizeImportName(item[spec.name])) > 1
        ? `${item[spec.name]} (ID ${item[spec.id]})` : item[spec.name],
    }));
    return [field, [...unique.values()]];
  })), [lookups]);
  const articleUnitIds = useMemo(() => new Set(options.unit.map(unit => unit.id)), [options]);
  const checkedRows = useMemo(() => {
    const duplicates = findImportDuplicates(rows);
    return rows.map(row => {
      const errors = { ...getImportRowErrors(row, duplicates), ...getImportServerErrors(row) };
      if (row.unitDetails && Object.keys(row.unitDetails).some(id => Object.keys(getImportCompanyUnitErrors(row,
        getImportCompanyUnitDetail(row, Number(id)), articleUnitIds)).length > 0))
        errors.companyUnits = "Një nga nënrreshtat ka gabime. Hapni njësitë për korrigjim.";
      return { row, errors, status: getArticleImportStatus(row, errors) };
    });
  }, [rows, articleUnitIds]);
  const statusCounts = useMemo(() => {
    const counts = { all: checkedRows.length, saved: 0, exists: 0, error: 0, pending: 0, ready: 0 };
    checkedRows.forEach(item => { counts[item.status]++; });
    return counts;
  }, [checkedRows]);
  const errorCount = statusCounts.error;
  const filtered = useMemo(() => {
    const query = normalizeImportName(search);
    return checkedRows.filter(item => (statusFilter === "all" || item.status === statusFilter) &&
      (!query || normalizeImportName(`${item.row.barcode} ${item.row.name}`).includes(query)));
  }, [checkedRows, statusFilter, search]);
  const visibleColumns = ARTICLE_TEMPLATE_COLUMNS;
  const safePage = Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1));
  const visible = useMemo(() => filtered.slice(safePage * pageSize, (safePage + 1) * pageSize), [filtered, safePage, pageSize]);

  const selectedCount = selection.all ? rows.length - selection.ids.size : selection.ids.size;
  const assignedCounts = useMemo(() => {
    const counts = new Map();
    rows.forEach(row => {
      if (isImportRowSelected(selection, row.id)) (row.companyUnitIds || []).forEach(id => counts.set(id, (counts.get(id) || 0) + 1));
    });
    return counts;
  }, [rows, selection]);
  const pageSelected = visible.length > 0 && visible.every(item => isImportRowSelected(selection, item.row.id));
  const pagePartial = !pageSelected && visible.some(item => isImportRowSelected(selection, item.row.id));
  const toggleRow = (id, selected) => setSelection(current => toggleImportRowSelection(current, id, selected));
  const togglePage = selected => setSelection(current => toggleImportRowsSelection(current,
    visible.map(item => item.row.id), selected));
  const toggleExpanded = id => setExpanded(current => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const updateRow = (id, transform) => {
    setValidationStale(true);
    setRows(previous => previous.map(row => row.id === id && !isImportRowLocked(row) ? transform(row) : row));
  };
  const saveAll = async () => {
    if (busy || bulkJob.current || !saveEnabled || !importId) return;
    const controller = new AbortController(); bulkJob.current = controller;
    const candidates = rows.filter(row => !isImportRowSaved(row));
    if (!candidates.length) { bulkJob.current = null; return; }
    setBusy(true); setSaving(true); onBusyChange?.(true); setBulkError(""); setNotice(""); setSaveReport(null); setValidationReport(null);
    setValidationProgress({ checked: 0, total: candidates.length });
    const duplicates = findImportDuplicates(rows);
    const result = await saveArticleImportBatches(candidates.map(row => ({ row, errors: getImportRowErrors(row, duplicates) })), articleUnitIds,
      { importId, showInPos, signal: controller.signal, save: requestArticleImportSave, onProgress: setValidationProgress,
        onBatchStart: payload => {
          const byExcel = new Map(payload.map(row => [row.ExcelRow, row]));
          setRows(current => current.map(row => byExcel.has(row.excelRow) ? { ...row, saveStatus: "Uncertain", savePayload: byExcel.get(row.excelRow) } : row));
        },
        onBatchResult: response => {
          const byExcel = new Map(response.map(row => [row.ExcelRow, row]));
          setRows(current => current.map(row => {
            const saved = byExcel.get(row.excelRow);
            return saved ? { ...row, saveStatus: saved.Status, savedArticleId: saved.ArticleID, serverIssues: saved.Issues,
              savePayload: ["Failed", "Exists"].includes(saved.Status) ? undefined : row.savePayload } : row;
          }));
        },
      });
    if (bulkJob.current !== controller) return;
    setRows(current => current.map(row => {
      const state = result.states.get(row.id);
      const issues = result.results.get(row.id);
      if (!state && !issues) return row;
      return { ...row, saveStatus: state?.status || "Failed", savedArticleId: state?.articleId,
        savePayload: state && !["Failed", "Exists"].includes(state.status) ? state.payload : undefined,
        serverIssues: issues || [{ ExcelRow: row.excelRow, Field: "Validation", Message: "Përgjigjja e ruajtjes mungon. Riprovoni Ruaj artikujt për ta verifikuar këtë rresht." }] };
    }));
    setSaveReport({ complete: result.complete, checked: result.checked, total: result.total });
    if (result.error) setBulkError(result.error);
    setValidationProgress(null); setBusy(false); setSaving(false); onBusyChange?.(false); bulkJob.current = null;
  };
  const validateAll = async () => {
    if (busy || bulkJob.current || !rows.length) return;
    const controller = new AbortController();
    bulkJob.current = controller;
    setBusy(true); onBusyChange?.(true); setBulkError(""); setNotice("");
    setValidationReport(null); setValidationProgress({ checked: 0, total: rows.length });
    const duplicates = findImportDuplicates(rows);
    const result = await validateArticleImportBatches(rows.filter(row => !isImportRowLocked(row)).map(row => ({ row, errors: getImportRowErrors(row, duplicates) })), articleUnitIds,
      { signal: controller.signal, onProgress: setValidationProgress, validate: requestArticleImportValidation });
    if (bulkJob.current !== controller) return;
    setRows(previous => previous.map(row => isImportRowLocked(row) ? row : ({ ...row, serverIssues: result.results.get(row.id) || [],
      validationChecked: result.results.has(row.id) })));
    setValidationReport(result); setValidationStale(false);
    if (result.error) setBulkError(result.error);
    setValidationProgress(null); setBusy(false); onBusyChange?.(false); bulkJob.current = null;
  };
  const addUnit = (row, unitId) => {
    if (!companyUnitIds.has(unitId)) return;
    updateRow(row.id, current => addImportCompanyUnit(current, unitId));
    setExpanded(current => new Set(current).add(row.id));
  };
  const changeUnitForSelected = async (unitId, checked) => {
    if (!selectedCount || busy) return;
    const controller = new AbortController();
    bulkJob.current = controller;
    setBusy(true); onBusyChange?.(true); setProgress(0); setNotice(""); setBulkError("");
    try {
      const editableSelection = { all: false, ids: new Set(rows.filter(row => !isImportRowLocked(row) && isImportRowSelected(selection, row.id)).map(row => row.id)) };
      const result = await assignImportCompanyUnit(rows, editableSelection, unitId, companyUnitIds,
        { signal: controller.signal, onProgress: setProgress, remove: !checked });
      if (controller.signal.aborted) return;
      setRows(result.rows);
      setValidationStale(true);
      setNotice(`${companyUnits.get(unitId)?.UnitName}: ${checked ? "u shtua në" : "u hoq nga"} ${result.changed.toLocaleString("sq")} artikuj.`);
      const first = visible.find(item => isImportRowSelected(selection, item.row.id));
      if (checked && first) setExpanded(current => new Set(current).add(first.row.id));
    } catch (error) {
      if (!controller.signal.aborted) setBulkError(error.message);
    } finally {
      if (!controller.signal.aborted) { setBusy(false); onBusyChange?.(false); bulkJob.current = null; }
    }
  };
  const removeRow = id => {
    if (rows.some(row => row.id === id && isImportRowLocked(row))) return;
    setValidationStale(true);
    setRows(previous => previous.filter(row => row.id !== id));
    setSelection(current => { const ids = new Set(current.ids); ids.delete(id); return { ...current, ids }; });
    setExpanded(current => { const next = new Set(current); next.delete(id); return next; });
  };

  const update = (id, field, value, lookupId) => {
    setValidationStale(true);
    setRows(previous => previous.map(row => {
      if (row.id !== id || isImportRowLocked(row)) return row;
      const sourceIssues = { ...row.sourceIssues };
      delete sourceIssues[field];
      const lookupOverrides = { ...row.lookupOverrides };
      if (LOOKUP_FIELDS[field]) {
        if (lookupId) lookupOverrides[field] = lookupId;
        else delete lookupOverrides[field];
      }
      return validateImportRow({ ...row, [field]: value, sourceIssues, lookupOverrides }, indexes);
    }));
  };
  return <Box className="article-import-controls" sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
    <div className="article-import-controls__statuses" aria-label="Filtro artikujt sipas statusit">
      {ARTICLE_IMPORT_STATUS_FILTERS.map(({ key, label, tone }) => {
        const Icon = { all: ListChecks, saved: CheckCircle2, exists: Info, error: CircleAlert, pending: Clock3, ready: FileCheck2 }[key];
        return <Button key={key} className={`article-import-controls__status article-import-controls__status--${tone}`}
          aria-pressed={statusFilter === key} aria-label={`Shfaq ${label.toLocaleLowerCase("sq")}: ${statusCounts[key]} artikuj`}
          onClick={() => { setStatusFilter(key); setPage(0); }}>
          <Icon size={19} /><span><strong>{statusCounts[key].toLocaleString("sq")}</strong><small>{label}</small></span>
        </Button>;
      })}
    </div>
    <div className="article-import-controls__toolbar">
      <TextField size="small" label="Kërko barkod ose emërtim" value={search}
        onChange={event => { setSearch(event.target.value); setPage(0); }} />
      <div className="article-import-controls__actions">
        <Button variant="outlined" disabled={busy || !rows.length} onClick={validateAll}>Kontrollo në databazë</Button>
        <Button variant="contained" disabled={busy || !saveEnabled || !rows.some(row => !isImportRowSaved(row))} onClick={saveAll}>{saving ? "Duke ruajtur…" : "Ruaj artikujt"}</Button>
      </div>
    </div>
    {busy && <Box className="article-import-controls__progress" aria-live="polite"><div><Typography role="status">{validationProgress
      ? `${saving ? "Duke ruajtur" : "Duke kontrolluar"}: ${validationProgress.checked.toLocaleString("sq")} nga ${validationProgress.total.toLocaleString("sq")} artikuj`
      : `Duke përpunuar njësitë: ${progress}%`}</Typography><strong>{Math.round(validationProgress
        ? validationProgress.checked / Math.max(validationProgress.total, 1) * 100 : progress)}%</strong></div>
      <LinearProgress variant="determinate" value={validationProgress ? validationProgress.checked / Math.max(validationProgress.total, 1) * 100 : progress} />
      <small>{saving ? "Procesi vazhdon automatikisht. Rreshtat problematikë shfaqen më poshtë; filtrat mund t’i përdorni gjatë ruajtjes." : "Kontrolli vazhdon automatikisht. Mund të shihni statuset pa ndërprerë procesin."}</small></Box>}
    <div className="article-import-controls__toggles">
      <FormControlLabel className={`article-import-controls__toggle ${showInPos ? "article-import-controls__toggle--pos" : ""}`}
        control={<Switch checked={showInPos} disabled={busy} inputProps={{ "aria-label": "Shfaq të gjithë në POS" }} onChange={event => onShowInPosChange?.(event.target.checked)} />}
        label={<span><strong>Shfaq të gjithë në POS</strong><small>Vlen për të gjithë artikujt e importimit</small></span>} />
      <FormControlLabel className={`article-import-controls__toggle ${statusFilter === "error" ? "article-import-controls__toggle--errors" : ""}`}
        control={<Switch checked={statusFilter === "error"} onChange={event => { setStatusFilter(event.target.checked ? "error" : "all"); setPage(0); }}
          inputProps={{ "aria-label": "Vetëm rreshtat me gabime" }} />}
        label={<span><strong>Vetëm rreshtat me gabime</strong><small>{errorCount} artikuj kërkojnë korrigjim</small></span>} />
    </div>
    <div className={`article-import-controls__selection ${selectedCount ? "article-import-controls__selection--active" : ""}`}>
      <div className="article-import-controls__selection-actions">
        <Button size="small" onClick={() => setSelection({ all: false, ids: new Set() })} disabled={busy || !selectedCount}>Pastro përzgjedhjen</Button>
      </div>
      <div className="article-import-controls__assignment">
        <span role="status">{selectedCount.toLocaleString("sq")} artikuj të zgjedhur</span>
      </div>
    </div>
    {!!companyUnits.size && <section className="article-import-controls__unit-buttons" aria-label="Njësitë organizative për artikujt e zgjedhur">
      <div><strong>Njësitë organizative</strong><span>{selectedCount ? "Zgjidhni një ose disa njësi për artikujt e përzgjedhur." : "Përzgjidhni artikujt në tabelë, pastaj caktoni njësitë."}</span></div>
      <div className="article-import-controls__unit-options">{[...companyUnits].map(([id, unit]) => {
        const count = assignedCounts.get(id) || 0;
        const checked = selectedCount > 0 && count === selectedCount;
        return <FormControlLabel key={id} className={`article-import-controls__unit-button ${checked ? "article-import-controls__unit-button--checked" : ""}`}
          control={<Checkbox checked={checked} indeterminate={count > 0 && count < selectedCount} disabled={!selectedCount || busy}
            inputProps={{ "aria-label": `Njësia ${unit.UnitName} për artikujt e zgjedhur`, "aria-checked": count > 0 && count < selectedCount ? "mixed" : checked }}
            onChange={event => changeUnitForSelected(id, event.target.checked)} />} label={unit.UnitName} />;
      })}</div>
      {!!companyUnits.size && <small>Shënimi shton njësinë; heqja e shënimit e largon nga artikujt e zgjedhur. Shenja e pjesshme tregon se vetëm disa e kanë këtë njësi.</small>}
    </section>}
    <div className="article-import-controls__hint">Përzgjedhja ruhet në të gjitha faqet. Klikoni në një fushë për ta ndryshuar; përdorni Enter për ta përfunduar.</div>
    {validationReport && <Alert severity={validationStale || !validationReport.complete || errorCount ? "warning" : "success"} sx={{ m: 2 }}>
      {validationStale ? "Të dhënat u ndryshuan. Klikoni përsëri “Kontrollo në databazë”; gabimet e mëparshme mbeten të shënuara deri në rikontroll."
        : `${validationReport.complete ? "Kontrolli përfundoi" : "Kontrolli nuk përfundoi"}: ${validationReport.checked} nga ${validationReport.total} artikuj u kontrolluan. ${errorCount} artikuj kanë gabime. Ky kontroll nuk ruan artikuj.`}
    </Alert>}
    {notice && <Alert severity="success" sx={{ m: 2 }}>{notice}</Alert>}
    {saveReport && <Alert severity={!saveReport.complete || statusCounts.error || statusCounts.pending ? "warning" : statusCounts.exists ? "info" : "success"} sx={{ m: 2 }}>
      {saveReport.complete ? "Përpunimi përfundoi." : "Përpunimi u ndërpre."} {statusCounts.saved} artikuj të ruajtur;
      {" "}{statusCounts.error} me gabime;
      {" "}{statusCounts.exists} ekzistojnë në databazë dhe u anashkaluan;
      {" "}{statusCounts.pending} në pritje të verifikimit.
      {" "}Riprovimi anashkalon rreshtat me sukses të konfirmuar. Nëse përgjigjja ka humbur, kontrollohen barkodat/emërtimet; rreshtat ekzistues shënohen për kontroll.
    </Alert>}
    {bulkError && <Alert severity="error" sx={{ m: 2 }}>{bulkError}</Alert>}
    <div className="article-import-controls__table-summary"><strong>{filtered.length.toLocaleString("sq")} artikuj në pamje</strong>
      <span>{ARTICLE_IMPORT_STATUS_FILTERS.find(filter => filter.key === statusFilter)?.label}{search.trim() ? ` · “${search.trim()}”` : ""}</span>
      {(statusFilter !== "all" || search.trim()) && <Button size="small" onClick={() => { setStatusFilter("all"); setSearch(""); setPage(0); }}>Pastro filtrat</Button>}
    </div>
    <Box component="fieldset" disabled={busy} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}><Paper elevation={0} sx={{ overflow: "hidden", borderRadius: 0 }}>
      <ArticleImportTreeGrid visible={visible} visibleColumns={visibleColumns} companyUnits={companyUnits}
        articleUnitIds={articleUnitIds} options={options} selection={selection} pageSelected={pageSelected} pagePartial={pagePartial}
        togglePage={togglePage} toggleRow={toggleRow} expanded={expanded} toggleExpanded={toggleExpanded}
        update={update} addUnit={addUnit} removeRow={removeRow} showInPos={showInPos}
        updateDetail={(rowId, unitId, field, value) => updateRow(rowId, current => updateImportCompanyUnitDetail(current, unitId, field, value))}
        removeUnit={(rowId, unitId) => updateRow(rowId, current => removeImportCompanyUnit(current, unitId))} />
      <TablePagination component="div" count={filtered.length} page={safePage} rowsPerPage={pageSize}
        rowsPerPageOptions={[25, 50, 100]} onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={event => { setPageSize(Number(event.target.value)); setPage(0); }}
        labelRowsPerPage="Artikuj për faqe:" labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`} />
    </Paper></Box>
  </Box>;
}
